/**
 * 4M なぜなぜ分析 (Why-Analysis) サービス層.
 *
 * 1つの事象 (事故・ヒヤリ・不具合) を入力として、Man / Machine / Material / Method
 * の4観点それぞれの 5段なぜなぜ + 根本原因 + 即時/恒久対策を Edge Function に問い合わせる.
 *
 * 既存 services/aiService.ts と同じ supabase.functions.invoke パターン.
 * デモモードでは organizationId='demo' を渡し、ユーザ未ログインでも体験できる.
 */

import { getSupabase, isSupabaseConfigured, extractFunctionError, NETWORK_ERROR_MESSAGE } from './supabase';
import { logger } from '../utils/logger';

export type FourMKey = 'man' | 'machine' | 'material' | 'method';

export interface Why {
  question: string;
  answer: string;
}

export interface Countermeasure {
  title: string;
  description: string;
}

export interface Analysis {
  whys: Why[];
  rootCause: string;
  immediate: Countermeasure;
  permanent: Countermeasure;
}

export interface WhyAnalysisResult {
  problem: string;
  analyses: Record<FourMKey, Analysis>;
  cached?: boolean;
}

const FOUR_M_LABELS: Record<FourMKey, string> = {
  man: '人',
  machine: '機械・設備',
  material: '材料・環境',
  method: '方法・管理',
};

export const FOUR_M_KEYS: FourMKey[] = ['man', 'machine', 'material', 'method'];

export const fourMLabel = (key: FourMKey) => FOUR_M_LABELS[key];

/**
 * Demo フォールバック: Edge Function 未デプロイ環境 (local 開発等) でも UI 検証できるよう、
 * 構造化されたモック結果を返す.
 */
function mockWhyAnalysis(problem: string): WhyAnalysisResult {
  const blank = (): Analysis => ({
    whys: Array.from({ length: 5 }, (_, i) => ({
      question: `なぜ ${i + 1}: ?`,
      answer: '(デモモード: API キー未設定のため、サンプル応答を表示しています)',
    })),
    rootCause: '(デモモード) 実際の AI 分析にはサーバ設定が必要です',
    immediate: { title: '応急策', description: 'デモ用のサンプル即時対策です。' },
    permanent: { title: '恒久対策', description: 'デモ用のサンプル恒久対策です。' },
  });
  return {
    problem,
    analyses: {
      man: blank(), machine: blank(), material: blank(), method: blank(),
    },
  };
}

/**
 * 4M なぜなぜ分析を実行する.
 *
 * @param problem 5〜200字の事象テキスト
 * @param organizationId 組織 ID (デモは 'demo' を渡す)
 */
export async function analyzeWhyChain(
  problem: string,
  organizationId: string
): Promise<WhyAnalysisResult> {
  const trimmed = problem.trim();
  if (trimmed.length < 5) {
    throw new Error('事象は5文字以上で入力してください');
  }
  if (trimmed.length > 200) {
    throw new Error('事象は200文字以内で入力してください');
  }

  const supabase = getSupabase();
  if (!supabase || !isSupabaseConfigured()) {
    logger.warn('Supabase not configured - returning mock why-analysis');
    return mockWhyAnalysis(trimmed);
  }

  const { data, error } = await supabase.functions.invoke('genbasafeai_why-analysis', {
    body: { problem: trimmed, organizationId },
  });

  if (error) {
    logger.error('genbasafeai_why-analysis failed', error);
    /* context は Response。中の { error } を読む。読めない場合でも
       SDK の英文は出さない。 */
    const ex = await extractFunctionError(error);
    throw new Error(ex.message ?? (ex.reached ? 'なぜなぜ分析に失敗しました' : NETWORK_ERROR_MESSAGE));
  }
  if (!data || !data.analyses) {
    throw new Error('AI からの応答が空でした');
  }

  return data as WhyAnalysisResult;
}

/**
 * サンプル事例プール (handover §6 から). UI で 4件ランダム表示し、再抽選可能に.
 */
export const SAMPLE_PROBLEMS: string[] = [
  '作業員が足場の開口部から墜落し負傷した',
  '掘削溝の土留めがたわみ、土砂が崩れかけた',
  '移動式クレーンの吊り荷が大きく振れ、作業員に接触しかけた',
  '高所作業中に工具を落下させ、下の通路に人がいた',
  '電動工具の充電中にコードが発熱し焦げ臭がした',
  '夏場の屋外作業で作業員が体調不良を訴えた',
  '玉掛けワイヤーの一部に素線切れが見つかった',
  'バックホウの旋回範囲に作業員が入り接触しかけた',
  '足場の組立中に手すりが未設置のまま上段で作業していた',
  '溶接作業の火花で近くの養生シートが焦げた',
  '高所からの資材揚重中に荷崩れが起きかけた',
  '雨天後のぬかるみで重機が傾き転倒しかけた',
  '活線近接作業で絶縁用保護具を着けずに接近した',
  '型枠支保工がコンクリート打設中にたわんだ',
  '脚立を最上段で使用し、バランスを崩して転落しかけた',
];

/** ランダムに n 件サンプルを抜き出す (再抽選用). */
export function pickRandomSamples(n: number): string[] {
  const shuffled = [...SAMPLE_PROBLEMS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}
